import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {FeatureList, GameConfig} from "../../../GameConfig";
import {TopReelFeature} from "./features/TopReelFeature";
import {ExtendedGameModel} from "../../../server/ExtendedGameModel";
import {HOTB} from "../../../HOTB";
import {gsap} from "gsap";
import {DeadFeature} from "./features/DeadFeature";
import {WildFeature} from "./features/WildFeature";
import {LaunchFeature} from "./features/LaunchFeature";
import {XNudgeFeature} from "./features/XNudgeFeature";
import {HOTBData} from "../../../server/HOTBData";
import {GameSounds} from "../../../sound/GameSounds";
import {CapFeature} from "./features/CapFeature";
import {ExtendedGameMode} from "../../../server/ExtendedGameMode";

export class TopReelView extends PIXI.Container implements StageLayer {
    private _maskArea: PIXI.Graphics;
    private _currentReelFeatures: (TopReelFeature | undefined)[] = [];

    constructor() {
        super();
        this.name = "TopReelView";
        this.init();
    }

    public show(): void {
        this.visible = true;
    }

    public hide(): void {
        this.visible = false;
    }

    // #327
    public hideIfPrevCapGame(): void {
        if (!(GameConfig.isBackFromAS) && HOTB.model.data.mode === ExtendedGameMode.CAPWIN_GAME) {
            for (let i: number = 0; i < this._currentReelFeatures.length; i++) {
                const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];

                if (currFeature) {
                    currFeature.alpha = 0;
                }
            }
        }
    }

    public onSpinStart(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < this._currentReelFeatures.length; i++) {
            const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];

            if (currFeature) {
                tl.add(currFeature.pushOut(), GameConfig.pushDelayBetweenItems * i);
            }
        }

        return tl;
    }

    public onGameLoad(): void {
        this.placeFeatures(0.001);
    }

    public onGameResult(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this.placeFeatures(GameConfig.pushDelayBetweenItems));

        return tl;
    }

    public removeDeadFeatures(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: (): void => {
                isDeadRemovalSoundPlaying = false;
                GameConfig.isDeadSkipped = false;
            }
        });

        let isDeadRemovalSoundPlaying: boolean = false;

        for (let i: number = 0; i < GameConfig.totalReels; i++) {
            const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];

            if (currFeature && currFeature.dropPosId > -1) {
                const featureName: string = currFeature.featureName;

                if (featureName === FeatureList.DEAD) {
                    tl.add([
                        currFeature.finishDrop(),
                        (): void => {
                            // play sound only once
                            if (!isDeadRemovalSoundPlaying) {
                                if (GameConfig.isDeadSkipped) {
                                    // HOTB.soundPlayer.playSound(GameSounds.DEAD_CRUSHED_SKIP);
                                } else {
                                    HOTB.soundPlayer.playSound(GameSounds.DEAD_CRUSHED);
                                }
                                isDeadRemovalSoundPlaying = true;
                            }
                        }
                    ], 0);
                    tl.add((<DeadFeature>currFeature).playSmokeOutAnim(), 0.45);
                    tl.add((): void => {
                        this.removeFeature(currFeature);
                    });
                }
            }
        }

        tl.timeScale(2);
        return tl;
    }

    public startDrop(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i: number = 0; i < GameConfig.totalReels; i++) {
            const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];


            if (currFeature && currFeature.dropPosId > -1) {
                if (currFeature?.featureName !== FeatureList.DEAD) {
                    const featureName: string = currFeature.featureName,
                        calculatedDropPosY: number = (currFeature.position.y + 3.5) + (currFeature.dropPosId * GameConfig.symbolHeight) + GameConfig.symbolHeight;

                    tl.add(currFeature.startDrop(calculatedDropPosY));
                    tl.add(currFeature.finishDrop());

                    tl.timeScale(1);

                    // todo FeatureList
                    switch (featureName) {
                        case FeatureList.XNUDGE:
                            tl.add((currFeature as XNudgeFeature).performNudge());
                            tl.add([(currFeature as XNudgeFeature).expandNudge(),
                                (): void => {
                                    HOTB.waysCounter.updateWaysCounterFromXNudge(i);
                                }]);
                            break;
                        default:
                            break;
                    }

                    tl.add((): void => {
                        this.removeFeature(currFeature);
                    });
                }
            }
        }

        return tl;
    }

    public reInit(): void {
        this.removeChildren();
        this.init();
        this.placeFeatures(0.0001);
    }

    private init(): void {
        this.position.set(0, 85);
        this.createMask();
    }

    private createMask(): void {
        this._maskArea = new PIXI.Graphics();
        this._maskArea.beginFill(0xFFFF00, 0.5);
        this._maskArea.drawRoundedRect(-23, 30, 747, 100, 38);
        this._maskArea.endFill();
        this._maskArea.beginFill(0xFFFF00, 0.5);
        this._maskArea.drawShape(new PIXI.Rectangle(0, 0, (GameConfig.symbolWidth * 7), 600));
        this._maskArea.position.set(10, 10);
        this._maskArea.endFill();
        this.mask = this._maskArea;
        this.addChild(this._maskArea);
    }

    private placeFeatures(delay: number): gsap.core.Timeline {
        HOTB.soundPlayer.playSound(GameSounds.PLACE_FEATURES_TOP_REEL);

        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            isRestore: boolean = gameModel.isRestore;

        let reelData: string[] = isRestore ? hotbData.reelTopAfterAvalanche : hotbData.reelTop;

        // #308
        if (hotbData.mode === ExtendedGameMode.CAPWIN_GAME && isRestore) {
            reelData = hotbData.reelTop;
        }

        if (hotbData.mode.includes("AVALANCHE") && !gameModel.isActionSpinToBonus) {
            this._currentReelFeatures.sort();

            for (let i: number = 0; i < this._currentReelFeatures.length; i++) {
                const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];

                if (currFeature) {
                    currFeature.updateAttributes(i);
                }
            }
        } else {
            this._currentReelFeatures = [];
        }

        let reelStartIndex: number = 0;

        // #274
        /*if (isRestore && hotbData.nextMode.includes("AVALANCHE")) {
            reelStartIndex = GameUtils.getMaxWinLength(hotbData.betWayWins);
        }*/

        for (let reelId: number = reelStartIndex; reelId < reelData.length; reelId++) {
            const featureName: string = reelData[reelId];

            if (featureName !== this._currentReelFeatures[reelId]?.featureName && featureName !== FeatureList.BLANK) {

                let featureItem: TopReelFeature;

                switch (featureName) {
                    case FeatureList.XNUDGE:
                        featureItem = new XNudgeFeature(reelId);
                        break;
                    case FeatureList.WILD:
                        featureItem = new WildFeature(reelId);
                        break;
                    case FeatureList.LAUNCH:
                        featureItem = new LaunchFeature(reelId);
                        break;
                    case FeatureList.DEAD:
                        featureItem = new DeadFeature(reelId);
                        break;
                    case FeatureList.MAGA_CAP:
                        featureItem = new CapFeature(reelId);
                        break;
                    default:
                        throw new Error(`check feature is implemented of not !!!\n [${HOTB.model.data.reelTop}]`);
                }

                this._currentReelFeatures[reelId] = featureItem;
                this.addChild(featureItem);
            }
        }

        for (let i: number = 0; i < this._currentReelFeatures.length; i++) {
            const currFeature: TopReelFeature | undefined = this._currentReelFeatures[i];

            if (currFeature) {
                tl.add(currFeature.pushIn(), (delay * i));
            }
        }

        if (gameModel.isActionSpinToBonus) {
            tl.totalProgress(1);
        }

        return tl;
    }

    private removeFeature(feature: TopReelFeature): void {
        if (this._currentReelFeatures[feature.reelId] === feature) {
            this._currentReelFeatures[feature.reelId] = undefined;
        } else {
            debugger;
        }

        this.removeChild(feature);
    }
}